import { UiFieldConditional } from '@/api/airchef/Api';
import { ItemTypeName } from '@/types/helpers';

export abstract class AirchefError extends Error {
  constructor(message?: string, options?: ErrorOptions) {
    super(message, options);
    this.name = this.constructor.name;
  }

  toString(): string {
    return new ToStringBuilder(this).toString();
  }
}

export class InvalidProjectError extends AirchefError {}
export class MissingRoomError extends AirchefError {}
export class InvalidContextError extends AirchefError {}

export class InvalidFileError extends AirchefError {
  allowOverride: boolean;
  jsonData: string | null = null;
  constructor(message: string, allowOverride = false, sourceFile?: string) {
    super(message);
    this.name = 'InvalidFileError';

    this.allowOverride = allowOverride;
    this.jsonData = sourceFile || null;
  }
}

export class MigrationNeededError extends AirchefError {
  jsonData: string;
  constructor(message: string, sourceFile: string) {
    super(message);
    this.jsonData = sourceFile;
  }
}
export class MigrationError extends AirchefError {}
export class ImportFileError extends AirchefError {}

export class EquationUpgradeError extends AirchefError {}

export class NoSuchApplianceError extends AirchefError {}
export class NoSuchProductError extends AirchefError {}
export class NoSuchAssetError extends AirchefError {
  // prettier-ignore
  constructor(typeOrMessage: ItemTypeName | ({} & string), message?: string) {// NOSONAR
    if (message) {
      super(message);
      this.name = `NoSuchAssetError [${typeOrMessage}]`;
    } else {
      super(typeOrMessage);
    }
  }
}
export class AssetAlreadyExistsError extends AirchefError {
  // prettier-ignore
  constructor(typeOrMessage: ItemTypeName | ({} & string), message?: string) { // NOSONAR
    if (message) {
      super(message);
      this.name = `AssetAlreadyExistsError [${typeOrMessage}]`;
    } else {
      super(typeOrMessage);
    }
  }
}

export class InvalidConversionError extends AirchefError {}
export class UnsupportedOperationError extends AirchefError {}
export class InvalidStateError extends AirchefError {}
export class InvalidSchemaError extends AirchefError {}
export class InvalidResponseError extends AirchefError {}

export class InvalidOperationError extends AirchefError {
  #op: string;
  constructor(message: string, operation: string) {
    super(message);
    this.#op = operation;
  }

  toString(): string {
    return new ToStringBuilder(this).addKeyValuePair('operation', this.#op).toString();
  }
}

export type InvalidProjectStateErrorOptions = ErrorOptions & {
  itemType?: string;
  itemId?: string;
  itemProperty?: string;
  sourceType?: string;
  sourceId?: string;
};

export class InvalidProjectStateError extends AirchefError {
  readonly #type?: string;
  readonly #id?: string;
  readonly #property?: string;
  readonly #sourceType?: string;
  readonly #sourceId?: string;
  constructor(msg?: string, options?: InvalidProjectStateErrorOptions) {
    super(msg, options);
    this.#id = options?.itemId;
    this.#type = options?.itemType;
    this.#sourceId = options?.sourceId;
    this.#sourceType = options?.sourceType;
    this.#property = options?.itemProperty;
  }

  toString(): string {
    return new ToStringBuilder(this)
      .addKeyValuePair('Item ID', this.#id, true)
      .addKeyValuePair('Item type', this.#type, true)
      .addKeyValuePair('Item property', this.#property, true)
      .addKeyValuePair('Source item type', this.#sourceType, true)
      .addKeyValuePair('Source item ID', this.#sourceId, true)
      .toString();
  }
}

export class DeviceValidationError extends AirchefError {
  invalidFields: string[] = [];
}

export type InvalidPathValueOptions = ErrorOptions & {
  path?: string;
  expectedType?: string;
  receivedType?: string;
};
export class InvalidPathValueError extends AirchefError {
  path?: string;
  expectedType?: string;
  receivedType?: string;
  name = 'InvalidPathValueError';

  constructor(message?: string, options?: InvalidPathValueOptions) {
    super(message, options);
    this.path = options?.path;
    this.expectedType = options?.expectedType;
    this.receivedType = options?.receivedType;
  }

  toString() {
    let msg = `${this.name}`;
    if (this.message) {
      msg += `: ${this.message}`;
    }
    if (this.path) {
      msg += ` [${this.path}]`;
    }

    if (this.expectedType || this.receivedType) {
      const exp = this.expectedType ? `expected: ${this.expectedType}` : '';
      const rec = this.receivedType ? `received: ${this.receivedType}` : '';

      const sep = exp && rec ? ', ' : '';

      msg += ` (${exp}${sep}${rec})`;
    }

    return msg;
  }
}

export class InvalidColumnConfigurationError extends AirchefError {
  readonly #column?: string;
  constructor(message?: string, col?: string) {
    super(message);
    this.#column = col;
  }

  toString(): string {
    return new ToStringBuilder(this).addKeyValuePair('column', this.#column).toString();
  }
}

export type InvalidUiConditionalOptions = ErrorOptions & {
  conditional: UiFieldConditional;
};
export class InvalidUiConditionalError extends AirchefError {
  name = 'InvalidUiConditionalError';
  conditional?: UiFieldConditional;

  constructor(message?: string, options?: InvalidUiConditionalOptions) {
    super(message, options);
    this.conditional = options?.conditional;
  }

  toString() {
    return new ToStringBuilder(this).addPart(this.message).addPart(this.conditional, true, true).toString();
  }
}

export class CircularDataError extends AirchefError {}

export class DbCacheError extends AirchefError {}

export type InvalidUiFieldErrorOptions = ErrorOptions & {
  uiFieldType: string;
};
export class InvalidUiFieldError extends AirchefError {
  field: string;
  constructor(message?: string, options?: InvalidUiFieldErrorOptions) {
    super(message, options);
    this.field = options?.uiFieldType ?? 'unknown';
  }

  toString(): string {
    return new ToStringBuilder(this).addKeyValuePair('fieldType', this.field).toString();
  }
}

export class InvalidUiRangeError extends AirchefError {
  constructor(
    readonly fieldPath: string,
    readonly reason: string,
  ) {
    super('Invalid generated range field configuration.');
  }

  toString(): string {
    return new ToStringBuilder(this)
      .addPart(this.message)
      .addKeyValuePair('fieldPath', this.fieldPath)
      .addKeyValuePair('reason', this.reason)
      .toString();
  }
}

class ToStringBuilder {
  readonly #error: AirchefError;
  readonly #parts: string[] = [];
  readonly #kvPairs: [string, string][] = [];
  constructor(error: AirchefError) {
    this.#error = error;

    if (this.#error.message) {
      this.addPart(this.#error.message);
    }
  }

  addPart(part?: unknown, parenthesis?: boolean, stringify?: boolean): this {
    if (part === undefined) return this;

    let base = stringify || typeof part !== 'string' ? JSON.stringify(part) : part;
    if (parenthesis) {
      base = `( ${base} )`;
    }
    this.#parts.push(base);
    return this;
  }

  addKeyValuePair(key: string, value?: string, omitIfMissing = false): this {
    if (omitIfMissing && value === undefined) return this;
    this.#kvPairs.push([key, value ?? 'unknown']);
    return this;
  }

  toString(): string {
    if (this.#parts.length === 0 && this.#kvPairs.length === 0) {
      return this.#error.name;
    }

    const finalParts: string[] = [];

    if (this.#kvPairs.length > 0) {
      const kvParts = this.#kvPairs.map(([k, v]) => `${k}: ${v}`).join(', ');
      finalParts.push(`[${kvParts}]`);
    }

    if (this.#parts.length > 0) {
      finalParts.push(this.#parts.join(', '));
    }

    return `${this.#error.name}: ${finalParts.join(' ')}`;
  }
}

export abstract class AirchefAuthError extends AirchefError {}

export class MissingClaimsError extends AirchefAuthError {}
export class MissingTokenError extends AirchefAuthError {}
export class NoActiveAccountError extends AirchefAuthError {}
